import {
  type gqlV2,
  useInvalidateHasuraQueries,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { ensure, hasValue } from '@vrplatform/utils';
import { getSyncStatus } from '@vrplatform/ui-common';
import { useCallback } from 'react';
import { useAutomationHistoryDrawer } from '../../hooks';
import { getSubtask, getWorkflowChangeRow } from './_utils';

export const STORYBOOK_AUTOMATION_HISTORY_SUBSCRIPTION_QUERY_KEY =
  'automation-history-subscription';

const getSyncSubscription = (
  sync: gqlV2.sync,
  { onlyErrors }: { onlyErrors: boolean }
) => {
  const changeErrors = sync
    .changes({
      where: {
        status: { _eq: 'failed' },
      },
      order_by: [
        {
          createdAt: 'desc_nulls_last',
        },
      ],
    })
    .map((x) => getWorkflowChangeRow(x, 'synced-from'));

  const subtaskErrors = sync
    .subtasks({
      where: {
        status: { _eq: 'failed' },
      },
      order_by: [
        {
          createdAt: 'desc_nulls_last',
        },
      ],
    })
    .map<(typeof changeErrors)[number]>(getSubtask);

  const errors = [...changeErrors, ...subtaskErrors];

  return {
    id: sync.id,
    status: getSyncStatus(sync),
    title: sync.message || '',
    triggerRef: sync.triggerRef,
    errors: {
      aggregate: errors.length,
      rowData: errors,
    },
    aggregateSyncedTo: onlyErrors
      ? 0
      : sync
          .changes_aggregate({
            where: {
              syncType: { _eq: 'push' },
            },
          })
          .aggregate?.count() || 0,
    aggregateSyncedFrom: onlyErrors
      ? 0
      : sync
          .changes_aggregate({
            where: {
              syncType: { _eq: 'pull' },
            },
          })
          .aggregate?.count() || 0,
  };
};

export type WorkflowSubscription = ReturnType<typeof getSyncSubscription>;

export function useAutomationHistoryDrawerSubscription(
  syncIds: string[],
  {
    onlyErrors,
  }: {
    onlyErrors: boolean;
  }
) {
  'use memo';

  const { refreshKeys } = useAutomationHistoryDrawer();
  const invalidate = useInvalidateHasuraQueries();
  const [{ id: tenantId }] = useTeam();

  const skip = !syncIds?.length;

  const onPollCompleted = useCallback(() => {
    const keys = refreshKeys?.filter(hasValue) || [];

    if (keys.length) {
      invalidate(keys as any[]);
    }
  }, [refreshKeys, invalidate]);

  const queryData = useQuery(
    (q, args) => {
      const syncs = !args.syncIds?.length
        ? []
        : q
            .syncs({
              where: {
                id: { _in: args.syncIds },
              },
              order_by: [{ createdAt: 'desc' }],
            })
            .map((sync) =>
              getSyncSubscription(sync, { onlyErrors: args.onlyErrors })
            );

      return {
        syncs,
      };
    },
    {
      queryKey: ['tasks', 'automations', 'jobPlans'],
      skip,
      keepPreviousData: true,
      variables: {
        syncIds,
        tenantId,
        onlyErrors,
      },
      storybookQueryKey: STORYBOOK_AUTOMATION_HISTORY_SUBSCRIPTION_QUERY_KEY,
      poll: {
        isCompleted: (data) => {
          const statuses = data?.syncs?.map((x) => x.status).filter(hasValue);
          return (
            !!statuses?.length &&
            statuses.every((x) =>
              ensure<(typeof x)[]>([
                'completed',
                'canceled',
                'failed',
              ]).includes(x)
            )
          );
        },
        onCompleted: onPollCompleted,
      },
    }
  );

  return {
    data: queryData.data?.syncs,
    isInitialLoading: queryData.isInitialLoading,
    error: queryData.error,
    refetch: queryData.refetch,
  };
}
